import { useEffect, useState } from "react";
import { Search, Star, X } from "lucide-react";
import Pagenation from "../../../../ecommerce/globalcomponents/pagenation/Pagenation";
import DateRangePicker from "../../../../ecommerce/globalcomponents/DateRangePicker";
import ReviewStoresTable from "./ReviewStoresTable";
import { useAppDispatch, useAppSelector } from "../../../../Redux/hooks";
import { RootState } from "../../../../store";
import {
  getMySubmittedReviews,
  deleteStoreReview,
} from "../../../../Redux/storeReview/slice";
import "./ReviewStoresPage.css";

const ReviewStores = () => {
  const dispatch = useAppDispatch();
  const { myReviews, meta, loading } = useAppSelector(
    (state: RootState) => state.storeReview,
  );

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [dateRange, setDateRange] = useState<{ from: string; to: string }>({
    from: "",
    to: "",
  });

  const buildQuery = (pageNum: number) => ({
    page: pageNum,
    limit,
    search: debouncedSearch || undefined,
    startDate: dateRange.from || undefined,
    endDate: dateRange.to || undefined,
  });

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    dispatch(getMySubmittedReviews(buildQuery(page)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, page, limit, debouncedSearch, dateRange]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm("Are you sure you want to delete this review?")) {
      const res = await dispatch(deleteStoreReview(id));
      if (res.meta.requestStatus === "fulfilled") {
        dispatch(getMySubmittedReviews(buildQuery(page)));
      }
    }
  };

  return (
    <section className="review-stores-page">
      <div className="review-stores-shell">
        <header className="review-stores-header">
          <div className="review-stores-heading">
            <span className="review-stores-heading-icon">
              <Star size={22} strokeWidth={2} />
            </span>
            <div>
              <span className="review-stores-kicker">MY STUFF</span>
              <h1>Review Stores</h1>
              <p>Manage the reviews you submitted for stores.</p>
            </div>
          </div>
          <span className="review-stores-count-chip">
            {meta?.total ?? myReviews.length} reviews
          </span>
        </header>

        <div className="review-stores-toolbar">
          <select
            value={limit}
            onChange={(e) => {
              setLimit(Number(e.target.value));
              setPage(1);
            }}
            aria-label="Reviews per page"
          >
            {[10, 25, 50, 75, 100].map((val) => (
              <option key={val} value={val}>
                Last {val} Entries
              </option>
            ))}
          </select>

          <div className="review-stores-date-picker">
            <DateRangePicker value={dateRange} onChange={setDateRange} />
          </div>

          <div className="review-stores-search">
            <Search size={17} strokeWidth={2} />
            <input
              type="search"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Search reviews..."
              aria-label="Search reviews"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                aria-label="Clear search"
              >
                <X size={15} />
              </button>
            )}
          </div>
        </div>

        <ReviewStoresTable
          reviews={myReviews}
          loading={loading}
          page={page}
          limit={limit}
          onDelete={handleDelete}
        />

        {meta && meta.totalPages > 1 && (
          <Pagenation
            currentPage={page}
            totalPages={meta.totalPages}
            onPageChange={handlePageChange}
          />
        )}
      </div>
    </section>
  );
};

export default ReviewStores;
